import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, Users, BookOpen } from 'lucide-react';
const features = ['Technical breakdowns for every core skill: serving, passing, setting, attacking, blocking, and defense.', 'Step-by-step coaching cues that players actually understand and respond to.', 'Common errors identified with instant, actionable corrections.', "The complete, authoritative reference guide you'll go back to all season long."];
export const WhatYouGet = () => {
  return <section className="py-12 md:py-24 bg-navy-800 text-white relative overflow-hidden" data-id="element-238" data-mp="1iq229k68vcp1">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-vbred/10 rounded-full blur-[120px] pointer-events-none" data-id="element-239" data-mp="141nzf98tvo8h" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-240" data-mp="1wqmkae4c54hh">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center max-w-3xl mx-auto mb-8 md:mb-12" data-id="element-241" data-mp="21hl59r09qqr2">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-3 md:mb-4 text-vbgold" data-id="element-242" data-mp="3565d8hw8wp2m">
            The Flagship Guide
          </h2>
          <p className="text-base md:text-xl font-medium text-gray-200" data-id="element-243" data-mp="30cu3a7w4zs6h">
            One comprehensive, 200-page operational manual with everything you
            need to prepare faster, coach better, and arrive at every practice
            completely ready.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-244" data-mp="3o3pzribjfbk2">
          {/* Left: Epic Mockup Showcase */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} whileHover={{
          scale: 1.02
        }} transition={{
          duration: 0.5
        }} className="relative w-full max-w-md mx-auto lg:max-w-none" data-id="element-245" data-mp="1tq6x5u2nh2rw">
            <div className="absolute inset-0 bg-gradient-to-tr from-vbgold/20 to-vbred/20 blur-2xl -z-10 rounded-full transform scale-90" data-id="element-246" data-mp="2nik44wimsd7l" />

            <div className="absolute -top-6 -right-6 md:-top-8 md:-right-8 bg-vbred text-white font-display text-xl md:text-2xl px-6 py-3 shadow-[0_10px_20px_rgba(230,57,70,0.4)] transform rotate-3 z-20 border-2 border-white/20 rounded-sm" data-id="element-247" data-mp="2xf6lhbbr0m4v">
              200 PAGES
            </div>

            <motion.img src="https://cdn.magicpatterns.com/uploads/eaqb19kyoyDZp6rC3AEpuf/Firefly_Gemini_Flash_quiero_que_me_adaptes_el_mockup_principal_a_los_colores_del_anuncio_que_te_pase_y_el_688253-Photoroom.png" alt="Volleyball Ready: The Complete Coaching Bible Mockup" className="w-full h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative z-10" animate={{
            y: [0, -12, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-248" data-mp="3b6bgu1832s9e" />

            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 px-6 py-2 rounded-full flex items-center gap-2 whitespace-nowrap z-20 shadow-lg" data-id="element-249" data-mp="ukn65daiwk48">
              <BookOpen className="w-4 h-4 text-vbgold" data-id="element-250" data-mp="1n92czcji3w4h" />
              <span className="text-sm font-bold tracking-wider uppercase text-gray-300" data-id="element-251" data-mp="1ihapxbzk08j3">
                The Complete Coaching Bible
              </span>
            </div>
          </motion.div>

          {/* Right: Copy & Features */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex flex-col space-y-5" data-id="element-252" data-mp="2ayn44ipt5zou">
            <div className="text-center" data-id="element-253" data-mp="1gs8utjx6o6ur">
              <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-3" data-id="element-254" data-mp="1lpsmpmmt0i56">
                Volleyball Ready:
                <br data-id="element-255" data-mp="29f3dqcecqy64" />
                <span className="text-vbgold" data-id="element-256" data-mp="29fva3pfrwzxg">The Complete Coaching Bible</span>
              </h3>

              {/* Social Proof Row */}
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 md:gap-6 mb-4 pb-4 border-b border-white/10" data-id="element-257" data-mp="2egap0lfpefhj">
                <div className="flex items-center gap-1" data-id="element-258" data-mp="24nldxro2f3mq">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-vbgold text-vbgold" data-id="element-259" data-mp="2sdfqs98dws6z" data-mpi={i} />)}
                  <span className="ml-2 font-bold text-lg" data-id="element-260" data-mp="2x5pj1vwz24cf">4.9/5</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300 font-medium" data-id="element-261" data-mp="lonqjwka9g90">
                  <Users className="w-5 h-5 text-vbred" data-id="element-262" data-mp="grnhrdw1tze7" />
                  Trusted by 2,000+ coaches worldwide 🌍
                </div>
              </div>

              <p className="text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-263" data-mp="vhl0vff4fywj">
                The foundation of everything. 200 pages of pure coaching gold.
                Stop reacting to your opponents and start building a program
                that wins. This is the ultimate operational system for serious
                volleyball coaches.
              </p>
            </div>

            <div className="space-y-3" data-id="element-264" data-mp="bpohicvlvilh">
              {features.map((feature, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4 + idx * 0.1
            }} whileHover={{
              x: 4,
              backgroundColor: 'rgba(15, 26, 46, 0.8)'
            }} className="flex items-start gap-3 bg-navy-900/50 p-3 rounded-lg border border-white/5 transition-colors duration-300" data-id="element-265" data-mp="7ow6pf7355n3" data-mpi={idx}>
                  <CheckCircle2 className="w-5 h-5 text-vbgold flex-shrink-0 mt-0.5" data-id="element-266" data-mp="228xfv125rig" />
                  <p className="text-sm md:text-base text-gray-200 font-medium" data-id="element-267" data-mp="64vqu0ddnkhr">
                    {feature}
                  </p>
                </motion.div>)}
            </div>

            <div className="bg-white/5 p-3 rounded-lg border-l-4 border-vbred italic text-gray-300 text-sm" data-id="element-268" data-mp="3aj3omd8htngp">
              "This single manual replaced three different binders I used to
              carry to practice. It's exactly what I needed to standardize how
              we teach skills across our entire club."
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};